import { Button, Card, CardContent, CardHeader, Stack, Typography } from '@mui/material'
import {
  CargoType,
  CarrierType,
  CarrierVisitDirection,
  CarrierVisitStatus,
} from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import {
  IOrderArchetypeGroupModel,
  VisitDashboardGeneralCargoTotalDefinition,
  VisitDashboardUnitTotalDefinition,
} from '@planning/utils/order-utils'
import { useTranslate } from '@tolgee/react'
import { ButtonOption, DashboardTile, OptionsButton, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { ReactNode, useCallback, useMemo } from 'react'
import { VisitOrdersArchetypeGroupsWrapper } from './VisitOrdersArchetypeGroupsWrapper'
import { VisitOrdersStateRange } from './VisitOrdersStateRange'
import { VisitOrdersStatusRange } from './VisitOrdersStatusRange'
import { VisitOrdersStatusText } from './VisitOrdersStatusText'

interface Props {
  title: string
  totalDefinition: VisitDashboardUnitTotalDefinition | VisitDashboardGeneralCargoTotalDefinition
  cargoType?: CargoType
  direction?: CarrierVisitDirection
  visitId?: number
  visitType?: CarrierType
  visitStatus?: CarrierVisitStatus
  archetypeGroups: IOrderArchetypeGroupModel[]
  isMinimalisticView?: boolean
  isTallyPage?: boolean
  onViewClick?: () => void
  onClickExportOrders?: (direction: CarrierVisitDirection) => void
  additionalContent?: ReactNode
  cardFooter?: ReactNode
}
export const VisitDashboardDirectionCard = observer(
  ({
    title,
    totalDefinition,
    cargoType,
    direction,
    visitId,
    visitType,
    visitStatus,
    archetypeGroups,
    isMinimalisticView,
    isTallyPage,
    onViewClick,
    onClickExportOrders,
    additionalContent,
    cardFooter,
  }: Props) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const { orderListUploadViewStoreV2 } = usePlanningStore()

    const isContainerType = cargoType === CargoType.Container

    const unitTotalDefinition = totalDefinition as VisitDashboardUnitTotalDefinition
    const generalCargoTotalDefinition = totalDefinition as VisitDashboardGeneralCargoTotalDefinition

    const onFileUpload = useCallback(async () => {
      orderListUploadViewStoreV2.reset()

      if (visitType === CarrierType.Train) {
        await orderListUploadViewStoreV2.setRailVisitId(visitId)
      } else {
        await orderListUploadViewStoreV2.setVesselVisitId(visitId)
      }

      if (direction) {
        orderListUploadViewStoreV2.setDirection(direction)
        orderListUploadViewStoreV2.setIsRestow(false)
      } else {
        orderListUploadViewStoreV2.setIsRestow(true)
      }

      orderListUploadViewStoreV2.setOpen(true)
    }, [direction, orderListUploadViewStoreV2, visitId, visitType])

    const actionOptions = useMemo(() => {
      const options: ButtonOption[] = []

      const isVisitLocked =
        visitStatus === CarrierVisitStatus.Completed ||
        visitStatus === CarrierVisitStatus.Departed ||
        visitStatus === CarrierVisitStatus.Cancelled ||
        visitStatus === CarrierVisitStatus.Closed

      if (onFileUpload) {
        options.push({
          label: t('uploadManifest', 'Upload {direction} manifest', { direction: title }),
          onClick: () => onFileUpload(),
          disabled: isVisitLocked,
        })
      }

      if (isContainerType) {
        if (onClickExportOrders && direction) {
          options.push({
            label: t('exportOrders', 'Export orders'),
            onClick: () => onClickExportOrders(direction),
          })
        }
      }

      return options
    }, [direction, title, visitStatus, onClickExportOrders, onFileUpload, t, isContainerType])

    const movementViewButtonCyId = () => {
      if (!direction) return 'view-vessel-visit-restow-btn'

      return direction === 'Inbound'
        ? 'view-vessel-visit-discharge-btn'
        : 'view-vessel-visit-loading-btn'
    }

    const totalLabel = useMemo(() => {
      if (isContainerType) {
        return t('total', 'TOTAL')
      }
      return t('totalOrders', 'Total orders')
    }, [isContainerType, t])

    return (
      <Card sx={{ minWidth: '100%', minHeight: '100%' }}>
        <CardHeader
          action={
            onViewClick !== undefined && (
              <Stack direction='row' gap={1}>
                <Button color='inherit' onClick={onViewClick} data-cy={movementViewButtonCyId()}>
                  {t('view', 'View')}
                </Button>
                {actionOptions.length > 0 && (
                  <OptionsButton
                    tooltip={t('moreOptions', 'More options')}
                    options={actionOptions}
                  />
                )}
              </Stack>
            )
          }
          title={<Typography variant='h2'>{title}</Typography>}
        />

        <CardContent sx={{ minHeight: '100%' }}>
          <Stack gap={2}>
            <Stack direction='row' gap={theme.customSpacing.xxl}>
              {isContainerType && (
                <DashboardTile label={t('teu', 'TEU')} value={unitTotalDefinition.teu ?? 0} />
              )}

              <DashboardTile
                label={totalLabel}
                labelDataCy={`order-direction-title-card-${direction ?? 'Restow'}-label`}
                value={totalDefinition.total ?? 0}
                valueDataCy={`order-direction-title-card-${direction ?? 'Restow'}-value`}
              />

              {!isContainerType && (
                <DashboardTile
                  label={t('totalWeight', 'Total weight')}
                  value={`${generalCargoTotalDefinition.totalWeight ?? 0} kg`}
                />
              )}
            </Stack>

            {totalDefinition.total ? (
              <>
                {(isTallyPage && (
                  <VisitOrdersStatusText
                    expected={totalDefinition.expected}
                    offTerminal={totalDefinition.offTerminal}
                    onTerminal={totalDefinition.onTerminal}
                    direction={direction ?? 'Restow'}
                  />
                )) || (
                  <VisitOrdersStatusRange
                    expected={totalDefinition.expected}
                    offTerminal={totalDefinition.offTerminal}
                    onTerminal={totalDefinition.onTerminal}
                    direction={direction}
                    valueSuffix={isContainerType ? '' : ' kg'}
                  />
                )}

                {!isMinimalisticView && (
                  <>
                    <VisitOrdersStateRange
                      full={unitTotalDefinition.full}
                      empty={unitTotalDefinition.empty}
                    />

                    <VisitOrdersArchetypeGroupsWrapper groups={archetypeGroups} title={title} />
                  </>
                )}
              </>
            ) : (
              <Typography variant='body2'>
                {t('noDataAvailableYet', 'No data available yet')}
              </Typography>
            )}

            {additionalContent}
          </Stack>
        </CardContent>

        {cardFooter}
      </Card>
    )
  },
)
